import React, { useCallback, useState } from 'react';
import { View, StyleSheet, LayoutChangeEvent } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  withTiming,
  runOnJS,
} from 'react-native-reanimated';
import {
  Gesture,
  GestureDetector,
  GestureHandlerRootView,
} from 'react-native-gesture-handler';

export interface DraggableListRenderItemInfo<T> {
  item: T;
  index: number;
  drag: () => void;
  isActive: boolean;
}

interface DraggableListProps<T> {
  data: T[];
  keyExtractor: (item: T, index: number) => string;
  renderItem: (info: DraggableListRenderItemInfo<T>) => React.ReactNode;
  onDragEnd: (data: { data: T[]; from: number; to: number }) => void;
  scrollEnabled?: boolean;
}

interface ItemMeasurement {
  height: number;
  y: number;
}

export function DraggableList<T>({
  data,
  keyExtractor,
  renderItem,
  onDragEnd,
}: DraggableListProps<T>) {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [itemHeights, setItemHeights] = useState<Map<number, number>>(new Map());
  
  const translateY = useSharedValue(0);
  const activeIndex = useSharedValue(-1);
  const startY = useSharedValue(0);
  
  const handleLayout = useCallback((index: number) => (event: LayoutChangeEvent) => {
    const { height } = event.nativeEvent.layout;
    setItemHeights(prev => {
      const next = new Map(prev);
      next.set(index, height);
      return next;
    });
  }, []);

  const getItemOffset = useCallback((index: number): number => {
    let offset = 0;
    for (let i = 0; i < index; i++) {
      offset += itemHeights.get(i) || 60; // Default height fallback
    }
    return offset;
  }, [itemHeights]);

  const getTargetIndex = useCallback((currentIndex: number, translationY: number): number => {
    const currentOffset = getItemOffset(currentIndex);
    const newOffset = currentOffset + translationY;
    
    let targetIndex = 0;
    let accumulatedOffset = 0;
    
    for (let i = 0; i < data.length; i++) {
      const itemHeight = itemHeights.get(i) || 60;
      if (newOffset < accumulatedOffset + itemHeight / 2) {
        targetIndex = i;
        break;
      }
      accumulatedOffset += itemHeight;
      targetIndex = i;
    }
    
    return Math.max(0, Math.min(targetIndex, data.length - 1));
  }, [data.length, itemHeights, getItemOffset]);

  const handleDragEnd = useCallback((fromIndex: number, toIndex: number) => {
    if (fromIndex !== toIndex) {
      const newData = [...data];
      const [removed] = newData.splice(fromIndex, 1);
      newData.splice(toIndex, 0, removed);
      onDragEnd({ data: newData, from: fromIndex, to: toIndex });
    }
    setDraggedIndex(null);
  }, [data, onDragEnd]);

  const createDragGesture = useCallback((index: number) => {
    return Gesture.Pan()
      .activateAfterLongPress(200)
      .onStart(() => {
        activeIndex.value = index;
        startY.value = getItemOffset(index);
        runOnJS(setDraggedIndex)(index);
      })
      .onUpdate((event) => {
        translateY.value = event.translationY;
      })
      .onEnd((event) => {
        const targetIndex = getTargetIndex(index, event.translationY);
        translateY.value = withSpring(0, { damping: 20, stiffness: 200 });
        activeIndex.value = -1;
        runOnJS(handleDragEnd)(index, targetIndex);
      })
      .onFinalize(() => {
        translateY.value = withTiming(0, { duration: 200 });
        activeIndex.value = -1;
        runOnJS(setDraggedIndex)(null);
      });
  }, [activeIndex, translateY, startY, getItemOffset, getTargetIndex, handleDragEnd]);

  return (
    <GestureHandlerRootView style={styles.container}>
      {data.map((item, index) => {
        const key = keyExtractor(item, index);
        const isActive = draggedIndex === index;
        const gesture = createDragGesture(index);

        return (
          <DraggableItem
            key={key}
            index={index}
            isActive={isActive}
            isDragging={draggedIndex !== null}
            draggedIndex={draggedIndex}
            translateY={translateY}
            itemHeights={itemHeights}
            onLayout={handleLayout(index)}
            gesture={gesture}
          >
            {renderItem({
              item,
              index,
              drag: () => {}, // Drag is handled by long press gesture
              isActive,
            })}
          </DraggableItem>
        );
      })}
    </GestureHandlerRootView>
  );
}

interface DraggableItemProps {
  index: number;
  isActive: boolean;
  isDragging: boolean;
  draggedIndex: number | null;
  translateY: Animated.SharedValue<number>;
  itemHeights: Map<number, number>;
  onLayout: (event: LayoutChangeEvent) => void;
  gesture: ReturnType<typeof Gesture.Pan>;
  children: React.ReactNode;
}

function DraggableItem({
  index,
  isActive,
  isDragging,
  draggedIndex,
  translateY,
  itemHeights,
  onLayout,
  gesture,
  children,
}: DraggableItemProps) {
  const animatedStyle = useAnimatedStyle(() => {
    if (isActive) {
      return {
        transform: [{ translateY: translateY.value }],
        zIndex: 100,
        shadowOpacity: 0.3,
        elevation: 8,
      };
    }

    // Move other items out of the way
    if (isDragging && draggedIndex !== null && draggedIndex !== index) {
      const draggedHeight = itemHeights.get(draggedIndex) || 60;
      const currentTranslation = translateY.value;
      
      // Calculate if this item should move
      if (draggedIndex < index) {
        // Dragged item is above, check if it's being dragged below this item
        if (currentTranslation > (index - draggedIndex) * draggedHeight - draggedHeight / 2) {
          return {
            transform: [{ translateY: withSpring(-draggedHeight, { damping: 20, stiffness: 200 }) }],
            zIndex: 1,
          };
        }
      } else {
        // Dragged item is below, check if it's being dragged above this item
        if (currentTranslation < -(draggedIndex - index) * draggedHeight + draggedHeight / 2) {
          return {
            transform: [{ translateY: withSpring(draggedHeight, { damping: 20, stiffness: 200 }) }],
            zIndex: 1,
          };
        }
      }
    }

    return {
      transform: [{ translateY: withSpring(0, { damping: 20, stiffness: 200 }) }],
      zIndex: 1,
    };
  }, [isActive, isDragging, draggedIndex, index, itemHeights]);

  return (
    <GestureDetector gesture={gesture}>
      <Animated.View
        style={[styles.item, animatedStyle, isActive && styles.activeItem]}
        onLayout={onLayout}
      >
        {children}
      </Animated.View>
    </GestureDetector>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  item: {
    backgroundColor: 'transparent',
  },
  activeItem: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowRadius: 8,
  },
});

// ScaleDecorator replacement - simple wrapper that applies scale animation
export function ScaleDecorator({ children }: { children: React.ReactNode }) {
  return <View>{children}</View>;
}
